// src/pages/Admin/ProductManager.jsx
// Product admin: compact searchable list + sectioned editor with a sticky save bar.
// Designed to work one-handed on a phone.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, getDocs, getDoc, setDoc, addDoc, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { normaliseSettings, isUsStoreReady, getGbpMatrix, getFromPrice, formatPrice } from '../utils/pricing';
import '../styles/ProductManager.css';
import '../styles/ProductAdmin.css';

// Firestore location of the store-wide US settings (on/off switch + US shipping)
const PRICING_SETTINGS_REF = () => doc(db, 'settings', 'pricing');

const CATEGORY_GROUPS = [
    { label: 'Signature Virgin Line', options: ['Hair Bundles', 'Frontals and Closures', 'Coloured Hair'] },
    { label: 'Pure Madness Raw Line', options: ['Pure Madness - Bundles', 'Pure Madness - Frontals'] },
    { label: 'Other', options: ['Hair Care', 'Accessories'] },
];
const CATEGORY_ORDER = CATEGORY_GROUPS.flatMap(g => g.options);

// ── Helpers ──────────────────────────────────────────────────────────────────
const isBlank = (v) => v === undefined || v === null || v === '';

// Strips blank/invalid entries and converts everything to numbers before saving.
const cleanMatrix = (matrix = {}, allowedKeys = null) => {
    const out = {};
    Object.entries(matrix).forEach(([key, value]) => {
        if (allowedKeys && !allowedKeys.has(key)) return; // option was removed
        if (isBlank(value)) return;
        const num = parseFloat(value);
        if (!isNaN(num) && num > 0) out[key] = num;
    });
    return out;
};

const lowestPrice = (matrix = {}) => {
    const values = Object.values(matrix).filter(v => !isNaN(v));
    return values.length ? Math.min(...values) : null;
};

// Every combination of the product's options, in display order (Type → Colour → Texture → Length).
// Keys stay "colour|length|texture|type" so existing prices in the database keep matching.
const buildMatrixRows = (fd) => {
    const tyArr = fd.optionTypes.length ? fd.optionTypes : [''];
    const cArr = fd.optionColors.length ? fd.optionColors : [''];
    const tArr = fd.optionTextures.length ? fd.optionTextures : [''];
    const lArr = fd.optionLengths.length ? fd.optionLengths : [''];
    const rows = [];
    tyArr.forEach(ty => cArr.forEach(c => tArr.forEach(t => lArr.forEach(l => {
        rows.push({ c, l, t, ty, key: `${c}|${l}|${t}|${ty}`, group: [ty, c].filter(Boolean).join(', ') || 'All options' });
    }))));
    return rows;
};

// Lace sizes the client sometimes typed into the Colour field
const LACE_KEYWORDS = ['13 x 4', '13 x 6', '13x4', '13x6', '4 x 4', '4x4', '5 x 5', '5x5', '6x6', 'frontal', 'closure'];
const isLaceType = (value) => value && LACE_KEYWORDS.some(k => value.toLowerCase().replace(/\s/g, '').includes(k.replace(/\s/g, '')));

const emptyForm = () => ({
    name: '', description: '', category: '', images: [],
    optionColors: [], optionLengths: [], optionTextures: [], optionTypes: [],
    pricingMatrix: {}, pricingMatrixUSD: {}, colorImages: {},
    colors: [], // legacy, kept for backward compatibility
    soldOut: false, featured: false, isActive: true,
});

// Loads a product into the editor, upgrading older data formats on the way.
const productToForm = (product) => {
    let oColors = [...(product.optionColors || [])];
    let oLengths = [...(product.optionLengths || [])];
    let matrix = { ...(product.pricingMatrix || {}) };
    let cImages = { ...(product.colorImages || {}) };

    if (!product.pricingMatrix && product.colors && typeof product.colors[0] === 'object') {
        product.colors.forEach(c => {
            if (!oColors.includes(c.name)) oColors.push(c.name);
            if (c.image) cImages[c.name] = c.image;
            c.lengths?.forEach(l => {
                const lenStr = l.length.toString();
                if (!oLengths.includes(lenStr)) oLengths.push(lenStr);
                matrix[`${c.name}|${lenStr}||`] = l.price;
            });
        });
    } else if (!product.pricingMatrix && product.variants && product.variants.length > 0) {
        product.variants.forEach(v => {
            const lenStr = v.length.toString();
            if (!oLengths.includes(lenStr)) oLengths.push(lenStr);
            matrix[`|${lenStr}||`] = v.price;
        });
    }

    // Very old products with no options: keep their single price
    if (Object.keys(matrix).length === 0 && oColors.length === 0 && oLengths.length === 0 && Number(product.price) > 0) {
        matrix['|||'] = product.price;
    }

    // Move lace sizes that were typed into the Colour field into Types
    const finalColors = [];
    const finalTypes = [...(product.optionTypes || [])];
    oColors.forEach(c => {
        if (isLaceType(c)) { if (!finalTypes.includes(c)) finalTypes.push(c); }
        else finalColors.push(c);
    });

    const remap = (m = {}) => {
        const out = {};
        Object.keys(m).forEach(oldKey => {
            let [c, l, t, ty] = oldKey.split('|');
            if (isLaceType(c)) { ty = c; c = ''; }
            out[`${c}|${l}|${t}|${ty}`] = m[oldKey];
        });
        return out;
    };

    return {
        name: product.name || '', description: product.description || '',
        category: product.category || '', images: product.images || [],
        optionColors: finalColors, optionLengths: oLengths,
        optionTextures: product.optionTextures || [], optionTypes: finalTypes,
        pricingMatrix: remap(matrix), pricingMatrixUSD: remap(product.pricingMatrixUSD || {}),
        colorImages: cImages, colors: product.colors || [],
        soldOut: product.soldOut || false, featured: product.featured || false, isActive: product.isActive !== false,
    };
};

// How many of a product's options have a $ price
const usCoverage = (product) => {
    const keys = Object.keys(getGbpMatrix(product));
    const usd = product.pricingMatrixUSD || {};
    const priced = keys.filter(k => Number(usd[k]) > 0).length;
    return { total: keys.length, priced, missing: keys.length - priced };
};

// ── Small components ─────────────────────────────────────────────────────────
const Switch = ({ checked, onChange, label, hint }) => (
    <button type="button" role="switch" aria-checked={checked} className="pm-switch" onClick={() => onChange(!checked)}>
        <span className="pm-switch-text">
            <span className="pm-switch-label">{label}</span>
            {hint && <span className="pm-switch-hint">{hint}</span>}
        </span>
        <span className={`pm-switch-track ${checked ? 'is-on' : ''}`} aria-hidden="true"><span className="pm-switch-thumb" /></span>
    </button>
);

const OptionChips = ({ label, hint, items, onAdd, onRemove, placeholder }) => {
    const inputRef = useRef(null);
    const add = () => {
        const v = inputRef.current?.value?.trim();
        if (v) { onAdd(v); inputRef.current.value = ''; inputRef.current.focus(); }
    };
    return (
        <div className="pm-field">
            <span className="pm-label">{label}</span>
            {hint && <span className="pm-hint">{hint}</span>}
            {items.length > 0 && (
                <div className="pm-chips">
                    {items.map((item, i) => (
                        <span key={`${item}-${i}`} className="pm-chip">
                            {item}
                            <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${item}`}>×</button>
                        </span>
                    ))}
                </div>
            )}
            <div className="pm-inline">
                <input
                    ref={inputRef}
                    type="text"
                    className="pm-input"
                    placeholder={placeholder}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                />
                <button type="button" className="pm-btn pm-btn-ghost" onClick={add}>Add</button>
            </div>
        </div>
    );
};

const MoneyInput = ({ symbol, value, onChange, missing, disabled, label }) => (
    <label className={`pm-money ${missing ? 'is-missing' : ''} ${disabled ? 'is-disabled' : ''}`}>
        <span className="pm-money-symbol" aria-hidden="true">{symbol}</span>
        <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="–"
            disabled={disabled}
            aria-label={label}
        />
    </label>
);

// ── US settings ──────────────────────────────────────────────────────────────
const UsSettingsPanel = ({ settings, onSave, onBack }) => {
    const [draft, setDraft] = useState(settings);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    useEffect(() => { setDraft(settings); }, [settings]);
    const set = (field, value) => { setSaved(false); setDraft(prev => ({ ...prev, [field]: value })); };

    const handleSave = async () => {
        setError('');
        const parsed = {
            usEnabled: Boolean(draft.usEnabled),
            usShippingUSD: isBlank(draft.usShippingUSD) ? null : parseFloat(draft.usShippingUSD),
            usFreeShippingOverUSD: isBlank(draft.usFreeShippingOverUSD) ? null : parseFloat(draft.usFreeShippingOverUSD),
        };
        if (parsed.usEnabled && (parsed.usShippingUSD === null || isNaN(parsed.usShippingUSD) || parsed.usShippingUSD < 0)) {
            return setError('Enter a US shipping fee before turning US sales on. Use 0 for free shipping.');
        }
        setSaving(true);
        try { await onSave(parsed); setSaved(true); }
        catch (err) { setError(`Couldn't save: ${err.message}`); }
        finally { setSaving(false); }
    };

    return (
        <div className="pm-editor">
            <button type="button" className="pm-back" onClick={onBack}>‹ Products</button>
            <h2 className="pm-title">US sales and shipping</h2>

            <section className="pm-section">
                <p className="pm-hint pm-hint-block">
                    When this is on, US visitors see $ prices and can check out with a US address.
                    A product option is only sold in the US if it has a $ price.
                </p>
                <Switch checked={Boolean(draft.usEnabled)} onChange={(v) => set('usEnabled', v)} label="Sell to US customers" />
                <div className="pm-grid-2">
                    <label className="pm-field">
                        <span className="pm-label">US shipping fee ($)</span>
                        <input className="pm-input" type="number" inputMode="decimal" step="0.01" min="0" value={draft.usShippingUSD ?? ''} onChange={(e) => set('usShippingUSD', e.target.value)} placeholder="e.g. 25" />
                    </label>
                    <label className="pm-field">
                        <span className="pm-label">Free US shipping over ($)</span>
                        <input className="pm-input" type="number" inputMode="decimal" step="0.01" min="0" value={draft.usFreeShippingOverUSD ?? ''} onChange={(e) => set('usFreeShippingOverUSD', e.target.value)} placeholder="Optional" />
                    </label>
                </div>
                {error && <p className="pm-error">{error}</p>}
                {saved && <p className="pm-ok">US settings saved.</p>}
            </section>

            <div className="pm-savebar">
                <span className="pm-savebar-status">{draft.usEnabled ? 'US sales will be on' : 'US sales will be off'}</span>
                <div className="pm-savebar-actions">
                    <button type="button" className="pm-btn pm-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save US settings'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Product editor ───────────────────────────────────────────────────────────
const ProductEditor = ({ formData, setFormData, isEditing, saving, error, onSave, onCancel, onDelete }) => {
    const [fxRate, setFxRate] = useState('');
    const [roundTo, setRoundTo] = useState('5');
    const [showMissingOnly, setShowMissingOnly] = useState(false);

    const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
    const addOption = (field, value) => setFormData(prev => prev[field].includes(value) ? prev : ({ ...prev, [field]: [...prev[field], value] }));
    const removeOption = (field, index) => setFormData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
    const setPrice = (matrixField, key, value) => setFormData(prev => ({ ...prev, [matrixField]: { ...(prev[matrixField] || {}), [key]: value } }));

    const rows = useMemo(() => buildMatrixRows(formData), [formData.optionTypes, formData.optionColors, formData.optionTextures, formData.optionLengths]); // eslint-disable-line react-hooks/exhaustive-deps
    const gbp = formData.pricingMatrix;
    const usd = formData.pricingMatrixUSD || {};

    const pricedCount = rows.filter(r => !isBlank(gbp[r.key])).length;
    const missingUsdCount = rows.filter(r => !isBlank(gbp[r.key]) && isBlank(usd[r.key])).length;

    // Group rows by Type/Colour for display
    const groups = useMemo(() => {
        const out = [];
        rows.forEach(r => {
            const last = out[out.length - 1];
            if (last && last.name === r.group) last.rows.push(r);
            else out.push({ name: r.group, rows: [r] });
        });
        return out;
    }, [rows]);

    // Copies prices into this group's empty boxes from the group above (matched by texture + length)
    const fillFromAbove = (groupIndex) => {
        const prev = groups[groupIndex - 1];
        const current = groups[groupIndex];
        if (!prev) return;
        setFormData(fd => {
            const nextGbp = { ...fd.pricingMatrix };
            const nextUsd = { ...(fd.pricingMatrixUSD || {}) };
            current.rows.forEach(r => {
                const match = prev.rows.find(p => p.t === r.t && p.l === r.l);
                if (!match) return;
                if (isBlank(nextGbp[r.key]) && !isBlank(fd.pricingMatrix[match.key])) nextGbp[r.key] = fd.pricingMatrix[match.key];
                if (isBlank(nextUsd[r.key]) && !isBlank(fd.pricingMatrixUSD?.[match.key])) nextUsd[r.key] = fd.pricingMatrixUSD[match.key];
            });
            return { ...fd, pricingMatrix: nextGbp, pricingMatrixUSD: nextUsd };
        });
    };

    // Fills $ prices from £ × rate, rounded up (e.g. to the nearest $5)
    const fillUsdPrices = (overwrite) => {
        const rate = parseFloat(fxRate);
        const step = parseFloat(roundTo) || 1;
        if (!(rate > 0)) return;
        if (overwrite && !window.confirm('Replace every $ price on this product?')) return;
        setFormData(prev => {
            const next = { ...(prev.pricingMatrixUSD || {}) };
            rows.forEach(r => {
                const g = parseFloat(prev.pricingMatrix[r.key]);
                if (isNaN(g)) return;
                if (!overwrite && !isBlank(next[r.key])) return;
                next[r.key] = Math.ceil(Math.round(g * rate * 100) / 100 / step - 1e-9) * step;
            });
            return { ...prev, pricingMatrixUSD: next };
        });
    };

    const rowLabel = (r) => [r.t, r.l].filter(Boolean).join(' ') || 'Price';
    const hasOptions = formData.optionTypes.length + formData.optionColors.length + formData.optionTextures.length + formData.optionLengths.length > 0;

    let status;
    if (pricedCount === 0) status = <span className="is-warn">Add at least one £ price</span>;
    else if (missingUsdCount > 0) status = <span className="is-warn">{missingUsdCount} {missingUsdCount === 1 ? 'option needs' : 'options need'} a $ price</span>;
    else status = <span className="is-good">All {pricedCount} options priced in £ and $</span>;

    return (
        <form className="pm-editor" onSubmit={onSave} noValidate>
            <button type="button" className="pm-back" onClick={onCancel}>‹ Products</button>
            <h2 className="pm-title">{isEditing ? (formData.name || 'Edit product') : 'New product'}</h2>

            {/* ── Details ── */}
            <section className="pm-section">
                <h3 className="pm-section-title">Details</h3>
                <label className="pm-field">
                    <span className="pm-label">Product name</span>
                    <input className="pm-input" type="text" value={formData.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Body Wave #1B" required />
                </label>
                <label className="pm-field">
                    <span className="pm-label">Category</span>
                    <select className="pm-input" value={formData.category} onChange={(e) => set('category', e.target.value)} required>
                        <option value="">Choose a category</option>
                        {CATEGORY_GROUPS.map(g => (
                            <optgroup key={g.label} label={g.label}>
                                {g.options.map(o => <option key={o} value={o}>{o}</option>)}
                            </optgroup>
                        ))}
                    </select>
                </label>
                <label className="pm-field">
                    <span className="pm-label">Description <span className="pm-optional">optional</span></span>
                    <textarea className="pm-input" rows="3" value={formData.description} onChange={(e) => set('description', e.target.value)} />
                </label>
                <div className="pm-switches">
                    <Switch checked={formData.isActive} onChange={(v) => set('isActive', v)} label="Show in shop" hint="Turn off to hide it without deleting" />
                    <Switch checked={formData.soldOut} onChange={(v) => set('soldOut', v)} label="Sold out" hint="Still shown, but can't be bought" />
                    <Switch checked={formData.featured} onChange={(v) => set('featured', v)} label="Featured" hint="Shown at the top of the shop" />
                </div>
            </section>

            {/* ── Photos ── */}
            <section className="pm-section">
                <h3 className="pm-section-title">Photos</h3>
                <p className="pm-hint pm-hint-block">Paste image links. The first photo is the main one.</p>
                {formData.images.map((url, index) => (
                    <div key={index} className="pm-photo-row">
                        <span className="pm-thumb">{url ? <img src={url} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /> : null}</span>
                        <input
                            className="pm-input"
                            type="url"
                            value={url}
                            onChange={(e) => { const next = [...formData.images]; next[index] = e.target.value; set('images', next); }}
                            placeholder="https://…"
                        />
                        <button type="button" className="pm-icon-btn" aria-label="Remove photo" onClick={() => set('images', formData.images.filter((_, i) => i !== index))}>×</button>
                    </div>
                ))}
                <button type="button" className="pm-btn pm-btn-ghost" onClick={() => set('images', [...formData.images, ''])}>Add photo</button>
            </section>

            {/* ── Options ── */}
            <section className="pm-section">
                <h3 className="pm-section-title">Options</h3>
                <p className="pm-hint pm-hint-block">The choices customers pick from. Leave any that don't apply empty.</p>
                <OptionChips label="Types" hint="e.g. 4x4 HD, 13x4 Frontal" items={formData.optionTypes} onAdd={(v) => addOption('optionTypes', v)} onRemove={(i) => removeOption('optionTypes', i)} placeholder="Add a type" />
                <OptionChips label="Colours" hint="e.g. 1B, 613 Blonde" items={formData.optionColors} onAdd={(v) => addOption('optionColors', v)} onRemove={(i) => removeOption('optionColors', i)} placeholder="Add a colour" />
                <OptionChips label="Textures" hint="e.g. Straight, Curly" items={formData.optionTextures} onAdd={(v) => addOption('optionTextures', v)} onRemove={(i) => removeOption('optionTextures', i)} placeholder="Add a texture" />
                <OptionChips label="Lengths" hint="e.g. 12 inches" items={formData.optionLengths} onAdd={(v) => addOption('optionLengths', v)} onRemove={(i) => removeOption('optionLengths', i)} placeholder="Add a length" />

                {formData.optionColors.length > 0 && (
                    <div className="pm-field">
                        <span className="pm-label">Photo for each colour <span className="pm-optional">optional</span></span>
                        {formData.optionColors.map(c => (
                            <div key={c} className="pm-photo-row">
                                <span className="pm-thumb">{formData.colorImages[c] ? <img src={formData.colorImages[c]} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /> : null}</span>
                                <span className="pm-photo-name">{c}</span>
                                <input className="pm-input" type="url" placeholder="https://…" value={formData.colorImages[c] || ''} onChange={(e) => set('colorImages', { ...formData.colorImages, [c]: e.target.value })} />
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* ── Prices ── */}
            <section className="pm-section">
                <h3 className="pm-section-title">Prices</h3>
                <p className="pm-hint pm-hint-block">
                    Leave £ empty if an option isn't sold. Options with a £ price but no $ price aren't shown to US customers.
                </p>

                <details className="pm-details">
                    <summary>Fill $ prices from £ prices</summary>
                    <div className="pm-details-body">
                        <div className="pm-grid-2">
                            <label className="pm-field">
                                <span className="pm-label">£1 equals ($)</span>
                                <input className="pm-input" type="number" inputMode="decimal" step="0.01" value={fxRate} onChange={(e) => setFxRate(e.target.value)} placeholder="e.g. 1.35" />
                            </label>
                            <label className="pm-field">
                                <span className="pm-label">Round up to nearest</span>
                                <select className="pm-input" value={roundTo} onChange={(e) => setRoundTo(e.target.value)}>
                                    <option value="1">$1</option>
                                    <option value="5">$5</option>
                                    <option value="10">$10</option>
                                </select>
                            </label>
                        </div>
                        <p className="pm-hint pm-hint-block">Use a rate a little above today's to cover US import duty and card fees.</p>
                        <div className="pm-inline pm-wrap">
                            <button type="button" className="pm-btn pm-btn-ghost" disabled={!(parseFloat(fxRate) > 0)} onClick={() => fillUsdPrices(false)}>Fill empty $ prices</button>
                            <button type="button" className="pm-btn pm-btn-quiet" disabled={!(parseFloat(fxRate) > 0)} onClick={() => fillUsdPrices(true)}>Replace all $ prices</button>
                        </div>
                    </div>
                </details>

                {hasOptions && missingUsdCount > 0 && (
                    <div className="pm-inline pm-filter-row">
                        <Switch checked={showMissingOnly} onChange={setShowMissingOnly} label={`Only show options missing a $ price (${missingUsdCount})`} />
                    </div>
                )}

                <div className="pm-prices" role="table" aria-label="Prices">
                    <div className="pm-price-head" role="row">
                        <span role="columnheader">Option</span>
                        <span role="columnheader">£ GBP</span>
                        <span role="columnheader">$ USD</span>
                    </div>

                    {groups.map((group, gi) => {
                        const visibleRows = showMissingOnly
                            ? group.rows.filter(r => !isBlank(gbp[r.key]) && isBlank(usd[r.key]))
                            : group.rows;
                        if (visibleRows.length === 0) return null;
                        return (
                            <div key={group.name} className="pm-price-group" role="rowgroup">
                                {(groups.length > 1 || group.name !== 'All options') && (
                                    <div className="pm-price-group-head">
                                        <span>{group.name}</span>
                                        {gi > 0 && !showMissingOnly && (
                                            <button type="button" className="pm-link-btn" onClick={() => fillFromAbove(gi)} title="Copies prices from the group above into empty boxes only">
                                                Fill blanks from above
                                            </button>
                                        )}
                                    </div>
                                )}
                                {visibleRows.map(r => {
                                    const hasGbp = !isBlank(gbp[r.key]);
                                    return (
                                        <div key={r.key} className="pm-price-row" role="row">
                                            <span className="pm-price-label" role="cell">{rowLabel(r)}</span>
                                            <MoneyInput symbol="£" value={gbp[r.key]} onChange={(v) => setPrice('pricingMatrix', r.key, v)} label={`${group.name} ${rowLabel(r)} price in pounds`} />
                                            <MoneyInput symbol="$" value={usd[r.key]} onChange={(v) => setPrice('pricingMatrixUSD', r.key, v)} missing={hasGbp && isBlank(usd[r.key])} disabled={!hasGbp} label={`${group.name} ${rowLabel(r)} price in dollars`} />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </section>

            {isEditing && (
                <section className="pm-section pm-danger">
                    <h3 className="pm-section-title">Delete product</h3>
                    <p className="pm-hint pm-hint-block">This can't be undone. To hide it for now, turn off "Show in shop" instead.</p>
                    <button type="button" className="pm-btn pm-btn-danger" onClick={onDelete}>Delete this product</button>
                </section>
            )}

            {/* ── Sticky save bar ── */}
            <div className="pm-savebar">
                <span className="pm-savebar-status">{error ? <span className="is-warn">{error}</span> : status}</span>
                <div className="pm-savebar-actions">
                    <button type="button" className="pm-btn pm-btn-quiet" onClick={onCancel}>Cancel</button>
                    <button type="submit" className="pm-btn pm-btn-primary" disabled={saving}>
                        {saving ? 'Saving…' : (isEditing ? 'Save changes' : 'Add product')}
                    </button>
                </div>
            </div>
        </form>
    );
};

// ── Main ─────────────────────────────────────────────────────────────────────
const FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'needsUsd', label: 'Needs $ prices' },
    { id: 'soldOut', label: 'Sold out' },
    { id: 'hidden', label: 'Hidden' },
    { id: 'featured', label: 'Featured' },
];

const ProductManager = ({ setMessage }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // 'list' | 'edit' | 'us'
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [toast, setToast] = useState('');
    const [pricingSettings, setPricingSettings] = useState(normaliseSettings());
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [filter, setFilter] = useState('all');
    const snapshotRef = useRef('');

    const showToast = (text) => { setToast(text); setTimeout(() => setToast(''), 3000); };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const snapshot = await getDocs(query(collection(db, 'products'), orderBy('name', 'asc')));
            setProducts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error('Error fetching products:', err);
            setMessage?.('Failed to load products.');
        } finally {
            setLoading(false);
        }
    };

    const fetchPricingSettings = async () => {
        try {
            const snap = await getDoc(PRICING_SETTINGS_REF());
            setPricingSettings(normaliseSettings(snap.exists() ? snap.data() : {}));
        } catch (err) {
            console.error('Error fetching US settings:', err);
        }
    };

    const savePricingSettings = async (values) => {
        await setDoc(PRICING_SETTINGS_REF(), { ...values, updatedAt: serverTimestamp() }, { merge: true });
        setPricingSettings(normaliseSettings(values));
    };

    useEffect(() => { fetchProducts(); fetchPricingSettings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const goTo = (next) => { setView(next); window.scrollTo({ top: 0 }); };

    const openEditor = useCallback((product) => {
        const fd = product ? productToForm(product) : emptyForm();
        setFormData(fd);
        snapshotRef.current = JSON.stringify(fd);
        setEditingProduct(product || null);
        setFormError('');
        goTo('edit');
    }, []);

    const closeEditor = () => {
        const dirty = JSON.stringify(formData) !== snapshotRef.current;
        if (dirty && !window.confirm('Discard your unsaved changes?')) return;
        setEditingProduct(null);
        goTo('list');
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setFormError('');

        if (!formData.name.trim()) return setFormError('Add a product name');
        if (!formData.category) return setFormError('Choose a category');

        const allowedKeys = new Set(buildMatrixRows(formData).map(r => r.key));
        const cleanedGbp = cleanMatrix(formData.pricingMatrix, allowedKeys);
        // A $ price only counts if the option also has a £ price
        const cleanedUsd = cleanMatrix(formData.pricingMatrixUSD, new Set(Object.keys(cleanedGbp)));

        if (Object.keys(cleanedGbp).length === 0) return setFormError('Add at least one £ price');

        const productData = {
            name: formData.name.trim(),
            description: formData.description,
            price: lowestPrice(cleanedGbp) ?? 0,
            priceUSD: lowestPrice(cleanedUsd),
            category: formData.category,
            images: formData.images.map(u => u.trim()).filter(Boolean),
            optionColors: formData.optionColors,
            optionLengths: formData.optionLengths,
            optionTextures: formData.optionTextures,
            optionTypes: formData.optionTypes,
            pricingMatrix: cleanedGbp,
            pricingMatrixUSD: cleanedUsd,
            colorImages: formData.colorImages,
            soldOut: formData.soldOut,
            featured: formData.featured,
            isActive: formData.isActive !== false,
        };

        setSaving(true);
        try {
            if (editingProduct) {
                await updateDoc(doc(db, 'products', editingProduct.id), productData);
                showToast('Changes saved');
            } else {
                await addDoc(collection(db, 'products'), { ...productData, createdAt: new Date() });
                showToast('Product added');
            }
            snapshotRef.current = JSON.stringify(formData);
            await fetchProducts();
            setEditingProduct(null);
            goTo('list');
        } catch (err) {
            setFormError(`Couldn't save: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!editingProduct) return;
        if (!window.confirm(`Delete "${editingProduct.name}"? This can't be undone.`)) return;
        try {
            await deleteDoc(doc(db, 'products', editingProduct.id));
            showToast('Product deleted');
            setEditingProduct(null);
            await fetchProducts();
            goTo('list');
        } catch (err) {
            setFormError(`Couldn't delete: ${err.message}`);
        }
    };

    // ── List data ──
    const enriched = useMemo(() => products.map(p => ({ ...p, _us: usCoverage(p) })), [products]);

    const counts = useMemo(() => ({
        all: enriched.length,
        needsUsd: enriched.filter(p => p.isActive !== false && p._us.missing > 0).length,
        soldOut: enriched.filter(p => p.soldOut).length,
        hidden: enriched.filter(p => p.isActive === false).length,
        featured: enriched.filter(p => p.featured).length,
    }), [enriched]);

    const categories = useMemo(() => {
        const present = [...new Set(enriched.map(p => p.category || 'Uncategorized'))];
        return [...CATEGORY_ORDER.filter(c => present.includes(c)), ...present.filter(c => !CATEGORY_ORDER.includes(c))];
    }, [enriched]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        return enriched.filter(p => {
            if (term && !(`${p.name} ${p.category}`.toLowerCase().includes(term))) return false;
            if (category !== 'all' && (p.category || 'Uncategorized') !== category) return false;
            if (filter === 'needsUsd') return p.isActive !== false && p._us.missing > 0;
            if (filter === 'soldOut') return p.soldOut;
            if (filter === 'hidden') return p.isActive === false;
            if (filter === 'featured') return p.featured;
            return true;
        });
    }, [enriched, search, category, filter]);

    const grouped = useMemo(() => {
        const map = {};
        visible.forEach(p => { const c = p.category || 'Uncategorized'; (map[c] = map[c] || []).push(p); });
        return categories.filter(c => map[c]).map(c => ({ category: c, items: map[c] }));
    }, [visible, categories]);

    const usOn = isUsStoreReady(pricingSettings);

    return (
        <div className="pm">
            {toast && <div className="pm-toast" role="status">{toast}</div>}

            {view === 'us' && (
                <UsSettingsPanel settings={pricingSettings} onSave={savePricingSettings} onBack={() => goTo('list')} />
            )}

            {view === 'edit' && (
                <ProductEditor
                    formData={formData}
                    setFormData={setFormData}
                    isEditing={Boolean(editingProduct)}
                    saving={saving}
                    error={formError}
                    onSave={handleSave}
                    onCancel={closeEditor}
                    onDelete={handleDelete}
                />
            )}

            {view === 'list' && (
                <>
                    <div className="pm-header">
                        <h2 className="pm-title">Products</h2>
                        <div className="pm-header-actions">
                            <button type="button" className={`pm-pill ${usOn ? 'is-on' : ''}`} onClick={() => goTo('us')}>
                                <span className="pm-dot" aria-hidden="true" />
                                US sales {usOn ? 'on' : 'off'}
                            </button>
                            <button type="button" className="pm-btn pm-btn-primary" onClick={() => openEditor(null)}>Add product</button>
                        </div>
                    </div>

                    <div className="pm-toolbar">
                        <input
                            type="search"
                            className="pm-input pm-search"
                            placeholder="Search products"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search products"
                        />
                        <select className="pm-input pm-category" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
                            <option value="all">All categories</option>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    <div className="pm-filters" role="tablist" aria-label="Filter products">
                        {FILTERS.map(f => (
                            <button
                                key={f.id}
                                type="button"
                                role="tab"
                                aria-selected={filter === f.id}
                                className={`pm-filter ${filter === f.id ? 'is-active' : ''} ${f.id === 'needsUsd' && counts.needsUsd > 0 ? 'is-alert' : ''}`}
                                onClick={() => setFilter(f.id)}
                            >
                                {f.label}<span className="pm-count">{counts[f.id]}</span>
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <p className="pm-empty">Loading products…</p>
                    ) : visible.length === 0 ? (
                        <div className="pm-empty">
                            {products.length === 0 ? (
                                <>
                                    <p>No products yet.</p>
                                    <button type="button" className="pm-btn pm-btn-primary" onClick={() => openEditor(null)}>Add your first product</button>
                                </>
                            ) : (
                                <>
                                    <p>Nothing matches these filters.</p>
                                    <button type="button" className="pm-btn pm-btn-ghost" onClick={() => { setSearch(''); setCategory('all'); setFilter('all'); }}>Clear filters</button>
                                </>
                            )}
                        </div>
                    ) : (
                        grouped.map(g => (
                            <section key={g.category} className="pm-list-group">
                                <h3 className="pm-list-heading">{g.category} <span>{g.items.length}</span></h3>
                                <ul className="pm-list">
                                    {g.items.map(p => {
                                        const fromGbp = getFromPrice(p, 'GBP');
                                        const fromUsd = getFromPrice(p, 'USD');
                                        return (
                                            <li key={p.id}>
                                                <button type="button" className={`pm-row ${p.isActive === false ? 'is-hidden' : ''}`} onClick={() => openEditor(p)}>
                                                    <span className="pm-row-img">
                                                        {p.images?.[0] && <img src={p.images[0]} alt="" loading="lazy" />}
                                                    </span>
                                                    <span className="pm-row-main">
                                                        <span className="pm-row-name">{p.name}</span>
                                                        <span className="pm-row-prices">
                                                            <span>{fromGbp !== null ? `From ${formatPrice(fromGbp, 'GBP')}` : 'No £ price'}</span>
                                                            <span className={fromUsd === null ? 'is-muted' : ''}>{fromUsd !== null ? formatPrice(fromUsd, 'USD') : 'No $ price'}</span>
                                                        </span>
                                                        <span className="pm-row-tags">
                                                            {p.isActive === false && <span className="pm-tag">Hidden</span>}
                                                            {p.soldOut && <span className="pm-tag is-red">Sold out</span>}
                                                            {p.featured && <span className="pm-tag is-gold">Featured</span>}
                                                            {p._us.total > 0 && (
                                                                p._us.missing === 0
                                                                    ? <span className="pm-tag is-green">$ prices complete</span>
                                                                    : <span className="pm-tag is-red">{p._us.missing} of {p._us.total} need $</span>
                                                            )}
                                                        </span>
                                                    </span>
                                                    <span className="pm-row-chevron" aria-hidden="true">›</span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        ))
                    )}

                    <div className="pm-footer">
                        <button type="button" className="pm-link-btn" onClick={() => { fetchProducts(); fetchPricingSettings(); }}>Refresh list</button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ProductManager;